import React, { useState, useEffect, useCallback, useRef } from 'react';
import { apiService } from '../../services/api';
import ExecutionInsightDetail from './ExecutionInsightDetail';
import './ExecutionInsights.css';

/**
 * ExecutionInsightsTab - Shows paginated list of past pipeline executions.
 * Each row is expandable to reveal full execution detail.
 */
function ExecutionInsightsTab({ graphId, focusInsightId, onFocusConsumed }) {
  const [insights, setInsights] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Guard against infinite retry when focusInsightId isn't in the current page
  const focusRetryRef = useRef(0);

  // Tracks which row is expanded and its full detail data
  const [expandedId, setExpandedId] = useState(null);
  const [expandedDetail, setExpandedDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const loadInsights = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.listExecutionInsights(graphId, page, pageSize);
      if (response.success) {
        setInsights(response.insights || []);
        setTotalCount(response.total_count || 0);
      } else {
        setError(response.error || 'Failed to load execution insights');
      }
    } catch (err) {
      console.error('Error loading execution insights:', err);
      const status = err?.response?.status;
      if (status === 429) {
        setError('Too many requests — please wait a moment and try again.');
      } else {
        setError('Failed to load execution insights');
      }
    } finally {
      setLoading(false);
    }
  }, [graphId, page, pageSize]);

  useEffect(() => {
    loadInsights();
  }, [loadInsights]);

  // Reset retry counter when the focus target changes
  useEffect(() => {
    focusRetryRef.current = 0;
  }, [focusInsightId]);

  // Auto-expand the focused insight when navigating from chat
  useEffect(() => {
    if (focusInsightId && insights.length > 0) {
      const found = insights.find(i => i.insight_id === focusInsightId);
      if (found && expandedId !== focusInsightId) {
        handleToggleExpand(focusInsightId);
        focusRetryRef.current = 0;
        onFocusConsumed?.();
      } else if (!found && focusRetryRef.current < 3) {
        focusRetryRef.current += 1;
        loadInsights();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusInsightId, insights]);

  // Handle expanding/collapsing a row
  const handleToggleExpand = async (insightId) => {
    if (expandedId === insightId) {
      // Collapse
      setExpandedId(null);
      setExpandedDetail(null);
      return;
    }

    // Expand — load full detail
    setExpandedId(insightId);
    setExpandedDetail(null);
    setDetailLoading(true);

    try {
      const response = await apiService.getExecutionInsight(insightId);
      if (response.success && response.insight) {
        setExpandedDetail(response.insight);
      } else {
        setExpandedDetail(null);
      }
    } catch (err) {
      console.error('Error loading insight detail:', err);
      setExpandedDetail(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const totalPages = Math.ceil(totalCount / pageSize);

  const formatTimestamp = (iso) => {
    if (!iso) return '-';
    const d = new Date(iso);
    return d.toLocaleString();
  };

  if (loading && insights.length === 0) {
    return (
      <div className="insights-container">
        <div className="insights-loading">Loading execution history...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="insights-container">
        <div className="insights-error">{error}</div>
        <button className="btn-secondary" onClick={loadInsights}>Retry</button>
      </div>
    );
  }

  if (insights.length === 0) {
    return (
      <div className="insights-container">
        <div className="insights-empty">
          <p>No execution history yet.</p>
          <p className="insights-empty-hint">Switch to the Chat tab and run some queries to see execution insights here.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="insights-container">
      <div className="insights-header">
        <h3>Execution History ({totalCount})</h3>
        <button className="btn-secondary btn-sm" onClick={loadInsights} disabled={loading}>
          {loading ? 'Refreshing...' : 'Refresh'}
        </button>
      </div>

      <div className="insights-list">
        {insights.map((insight) => (
          <div key={insight.insight_id} className={`insight-row ${expandedId === insight.insight_id ? 'expanded' : ''}`}>
            {/* Collapsed row summary */}
            <button
              className="insight-row-header"
              onClick={() => handleToggleExpand(insight.insight_id)}
            >
              <span className={`expand-arrow ${expandedId === insight.insight_id ? 'open' : ''}`}>
                &#9654;
              </span>
              <span className="insight-query" title={insight.query}>
                {insight.query.length > 80 ? insight.query.substring(0, 80) + '...' : insight.query}
              </span>
              <span className="insight-meta-pills">
                {insight.score != null && (
                  <span className="pill pill-score">{(insight.score * 100).toFixed(0)}%</span>
                )}
                {insight.duration_ms != null && (
                  <span className="pill pill-duration">{insight.duration_ms.toFixed(0)}ms</span>
                )}
                {insight.total_spikes != null && insight.total_spikes > 0 && (
                  <span className="pill pill-spikes">{insight.total_spikes} selections</span>
                )}
                {insight.llm_model && (
                  <span className="pill pill-model">{insight.llm_model}</span>
                )}
              </span>
              <span className="insight-timestamp">{formatTimestamp(insight.created_at)}</span>
            </button>

            {/* Expanded detail */}
            {expandedId === insight.insight_id && (
              <div className="insight-detail-wrapper">
                {detailLoading ? (
                  <div className="insights-loading">Loading trace details...</div>
                ) : expandedDetail ? (
                  <ExecutionInsightDetail insight={expandedDetail} graphId={graphId} />
                ) : (
                  <div className="insights-error">Failed to load trace details.</div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="insights-pagination">
          <button
            className="btn-secondary btn-sm"
            disabled={page <= 1}
            onClick={() => setPage(p => p - 1)}
          >
            Previous
          </button>
          <span className="pagination-info">Page {page} of {totalPages}</span>
          <button
            className="btn-secondary btn-sm"
            disabled={page >= totalPages}
            onClick={() => setPage(p => p + 1)}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}

export default ExecutionInsightsTab;
